
import { useState, useRef, useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom'

//Components
import { Grid } from "@mui/material";
import {
    Modal,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    Form,
    Select,
} from "@gpstrackit/react-controls";
import CustomCard from 'Components/Card';
import { copyToClipboard } from "Core/data/Helpers"

//Notification
import { NotificationManager } from "Components/Notifications";

//Actions
import {
    changeSimProviderGetDeviceRequest,
    searchSimProviderAccountsRequest,
    changeSimProviderSetDevice,
    changeSimProviderRequest,
    clearRedux,
} from 'Redux/actions';

const ChangeSimProvider = (props) => {
    const formRef = useRef(null);
    const formTwoRef = useRef(null);
    const { messages } = useIntl();
    const { tableRef, single, getDevice } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate()

    //Redux States
    const { device, loading, openModal, result, devices, selectedDevices } = useSelector(state => state.changeSimProviderRedux)
    const { simProviderAccounts } = useSelector(state => state.generalRedux);

    //Local States
    const [serial, setSerial] = useState("");
    const [step, setStep] = useState(1);
    const [simProviderAccountsList, setSimProviderAccounts] = useState([]);
    const [simProvider, setSimProvider] = useState(null);

    const searchDevice = () => {
        dispatch(changeSimProviderGetDeviceRequest(serial?.trim()));
    }

    const closeModal = () => {
        setSerial("");
        setStep(1);
        setSimProviderAccounts([]);
        setSimProvider(null)
        dispatch(clearRedux("CHANGE_SIM_PROVIDER"));
    }

    const applyChanges = () => {
        let unitIds = devices.map((item) => (item?.id))
        let payload = {
            features: {},
            simProviderAccountId: simProvider?.id,
            unitIds
        }
        dispatch(changeSimProviderRequest(payload))
    }

    useEffect(() => {
        if (device) {
            if (!device?.total) {
                NotificationManager.warning(
                    messages['change_sim_provider_device_has_not_been_found'],
                    messages['change_sim_provider_title'],
                    3000
                );
            } else {
                let currentDevices = devices;
                let newDevice = device?.items[0];
                let validateNewDevice = currentDevices.some((item) => item.id === newDevice.id);
                if (!validateNewDevice) {
                    let validateGlobalStar = newDevice?.deviceModel?.manufacturer?.name !== "Global Star";
                    if (validateGlobalStar) currentDevices.push(newDevice);
                    setSerial("");
                    dispatch(changeSimProviderSetDevice(currentDevices))
                } else {
                    setSerial("");
                    NotificationManager.warning(
                        messages['change_sim_provider_device_is_already_added'],
                        messages['change_sim_provider_title'],
                        3000
                    );
                }
            }
            dispatch(clearRedux("CHANGE_SIM_PROVIDER", { device: null }))
        }
        if (result?.updateSimprovider) {
            let transactionId = result?.updateSimprovider?.transactionId;
            let message = messages["change_sim_provider_message"]
            NotificationManager.success(message, messages['change_sim_provider_title'], 10000, () => {
                copyToClipboard(transactionId, messages['common_copied']);
                navigate(`/app/transactions/list/${transactionId}`);

            }, null, '', null);

            closeModal();
            getDevice && getDevice();
            tableRef && tableRef?.current?.getData();
        } else if (result && !result?.updateSimprovider) {
            NotificationManager.error(
                messages['change_sim_provider_error_message'],
                messages['change_sim_provider_title'],
                3000
            );
            dispatch(clearRedux("CHANGE_SIM_PROVIDER", { result: null }))
        }
        if (selectedDevices && selectedDevices.length > 0) {
            let newDevices = selectedDevices.map((item) => ({ id: item?.id, imei: item?.imei, esn: item?.esn, deviceModel: item?.deviceModel }));
            dispatch(changeSimProviderSetDevice(newDevices));
            dispatch(clearRedux("CHANGE_SIM_PROVIDER", { selectedDevices: [] }))
        }
    }, [device, result, selectedDevices])

    useEffect(() => {
        if (openModal) dispatch(searchSimProviderAccountsRequest())
    }, [openModal])

    useEffect(() => {
        if (openModal && simProviderAccounts) {
            if (simProviderAccounts && simProviderAccounts.length > 0) {
                let current = simProviderAccounts.map((item) => ({ ...item, label: `${item.name} ${item?.simProvider?.name ? `- ${item?.simProvider?.name}` : ""}` }));
                setSimProviderAccounts(current);
            }
        }
    }, [simProviderAccounts])


    let validateStepOne = step === 1 && !single
    let validateStepTwo = step === 2 || single

    return (
        <Modal
            open={openModal}
            title={messages['change_sim_provider_title']}
            position="right"
            size="small"
            handleClose={closeModal}
            loading={loading}
        >
            <ModalBody>
                {
                    validateStepOne &&
                    <div style={{ padding: '0 10px' }}>
                        <Grid
                            container
                            spacing={1}
                        >
                            <Grid item xs={10}>
                                <Form
                                    ref={formRef}
                                    onValidSubmit={() => searchDevice()}
                                >
                                    <Input
                                        label={messages['change_sim_provider_input']}
                                        placeholder={messages['change_sim_provider_input']}
                                        value={serial}
                                        required
                                        requiredMessage={messages['required_field']}
                                        onChange={(value) => setSerial(value)}
                                    />
                                </Form>
                            </Grid>
                            <Grid item xs={2}>
                                <div style={{ marginTop: 26 }}>
                                    <Button
                                        className="btn-dark-blue"
                                        onClick={() => formRef?.current?.submit()}
                                    >
                                        {messages['common_add']}
                                    </Button>
                                </div>
                            </Grid>
                        </Grid>
                        {devices?.length > 0 ? devices.map((item, index) => {
                            return (
                                <CustomCard
                                    key={index}
                                    onClick={() => {
                                        let currentDevices = devices;
                                        let selected = currentDevices.findIndex((device) => device.id === item.id);
                                        if (selected > -1) currentDevices.splice(selected, 1);
                                        dispatch(changeSimProviderSetDevice(currentDevices));
                                    }}
                                >
                                    <div style={{ display: 'grid' }}>
                                        <div>
                                            <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['change_sim_provider_imei']}</label><span>{item.imei}</span>
                                        </div>
                                        <div>
                                            <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['change_sim_provider_serial']}</label><span>{item.esn}</span>
                                        </div>
                                        <div>
                                            <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['change_sim_provider_model']}</label><span>{item?.deviceModel?.name}</span>
                                        </div>
                                    </div>
                                </CustomCard>
                            )
                        }) : <div style={{ display: 'flex', justifyContent: 'center', marginTop: 25 }} ><span style={{ opacity: 0.6 }} >{messages['common_no_devices_added']}</span></div>}
                    </div>
                }
                {
                    validateStepTwo &&
                    <div style={{ padding: '0 10px' }}>
                        <Form
                            ref={formTwoRef}
                            onValidSubmit={() => applyChanges()}
                        >
                            <Select
                                id="list-new-simprovider"
                                value={simProvider}
                                options={simProviderAccountsList}
                                label={messages['change_sim_provider_account_label']}
                                placeholder={messages['change_sim_provider_account_label']}
                                onChange={(item) => setSimProvider(item)}
                                valueField="id"
                                textField="label"
                                required
                                requiredMessage={messages['required_field']}
                            />
                        </Form>
                    </div>
                }
            </ModalBody>
            <ModalFooter>
                <Button
                    className="btn-cancel"
                    onClick={() => closeModal()}
                >
                    {messages['common_cancel']}
                </Button>
                {step === 2 && !single &&
                    <Button
                        className="btn btn-cancel btn-gray"
                        onClick={() => setStep(1)}
                    >
                        {messages['common_back']}
                    </Button>
                }
                <Button
                    disabled={step === 1 && devices.length === 0}
                    className="btn-dark-blue"
                    onClick={() => {
                        if (single) formTwoRef?.current?.submit();
                        else {
                            if (step < 2) setStep(step + 1);
                            else formTwoRef?.current?.submit();
                        }
                    }}
                >
                    {step === 1 && !single ? messages['common_next'] : messages['change_sim_provider_change']}
                </Button>
            </ModalFooter>
        </Modal >
    )
}

export default ChangeSimProvider;